import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useLocation } from 'react-router-dom';
import { useInvoiceLogsQuery, InvoiceLogApiItem } from './invoiceLogHelper';
import { TABLE_HEADER_ROW_SX } from '../../../../utils/typographyStyles';
import useCurrency from '../../../../components/CurrencyProvider';
import { formatDateTime } from '../../../../utils/dateUtils';
import InvoicePagination from '../../../../components/Billing/Invoice/InvoicePagination';
import InvoiceSearchBar from '../../../../components/Billing/Invoice/InvoiceSearchBar';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 15, 25, 50];

const LABELS = {
  title: 'Invoice Logs',
  searchPlaceholder: 'Search By Patient Name, Invoice No., Provider',
  loading: 'Loading...',
  error: 'Failed to load invoice logs.',
  noData: 'No invoice logs found.',
  updating: '(updating...)',
};

const getHeaderCells = (currencySymbol: string) => [
  { key: 'dateTime', label: 'Date & Time', align: 'left' as const },
  { key: 'action', label: 'Action', align: 'left' as const },
  { key: 'user', label: 'User', align: 'left' as const },
  { key: 'totalAmt', label: `Total Amt (${currencySymbol})`, align: 'right' as const },
  { key: 'concessionAmt', label: `Concession Amt (${currencySymbol})`, align: 'right' as const },
  { key: 'taxAmt', label: `Tax Amt (${currencySymbol})`, align: 'right' as const },
  { key: 'netAmt', label: `Net Amt (${currencySymbol})`, align: 'right' as const },
  { key: 'paidAmt', label: `Paid Amt (${currencySymbol})`, align: 'right' as const },
  { key: 'balanceAmt', label: `Balance Amt (${currencySymbol})`, align: 'right' as const },
  { key: 'status', label: 'Status', align: 'center' as const },
];

const STATUS_STYLES = {
  paid: {
    label: 'Paid',
    borderColor: '#16A34ACC',
    bgcolor: '#ECFDF3',
    color: '#15803D',
  },
  unpaid: {
    label: 'Unpaid',
    borderColor: '#F97316CC',
    bgcolor: '#FFF7ED',
    color: '#B45309',
  },
  draft: {
    label: 'Draft',
    borderColor: '#9CA3AFCC',
    bgcolor: '#F3F4F6',
    color: '#4B5563',
  },
} as const;

// ============================================================================
// Types
// ============================================================================

interface LocationState {
  invoice_id?: number | string | null;
}

interface InvoiceLogRow {
  id: number;
  historyDate: string;
  action: string;
  userName: string;
  status: keyof typeof STATUS_STYLES;
  totalAmount: number;
  concessionAmount: number;
  taxAmount: number;
  netAmount: number;
  paidAmount: number;
  balanceAmount: number;
}

// ============================================================================
// Helpers
// ============================================================================

const mapApiItemToRow = (item: InvoiceLogApiItem): InvoiceLogRow => ({
  id: item.history_id,
  historyDate: formatDateTime(item.history_date),
  action: item.history_type_display,
  userName: item.user_name,
  status: item.status,
  totalAmount: Number(item.total_amount) || 0,
  concessionAmount: Number(item.concession_amount) || 0,
  taxAmount: Number(item.tax_amount) || 0,
  netAmount: Number(item.net_amount) || 0,
  paidAmount: Number(item.paid_amount) || 0,
  balanceAmount: Number(item.balance_amount) || 0,
});

// ============================================================================
// Component
// ============================================================================

const InvoiceLogs: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceId = (location.state as LocationState | null)?.invoice_id ?? null;
  const { formatCurrency, currencySymbol } = useCurrency();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Generate header cells with currency symbol
  const HEADER_CELLS = useMemo(() => getHeaderCells(currencySymbol), [currencySymbol]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const { data, isLoading, error } = useInvoiceLogsQuery({
    invoiceId,
    page,
    limit: pageSize,
    search,
    enabled: !!invoiceId,
  });

  const totalCount = data?.meta.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const tableRows = useMemo<InvoiceLogRow[]>(() => {
    if (!data?.objects?.length) return [];
    return data.objects.map(mapApiItemToRow);
  }, [data]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when page size changes
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mx: 2, my: 2 }}>
          <IconButton
            size="small"
            onClick={handleGoBack}
            sx={{ mr: 1.5, borderRadius: 2, border: '1px solid #E5E7EB' }}
            aria-label="Go back"
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1, color: '#111827' }}>
            {LABELS.title}
          </Typography>

          <InvoiceSearchBar
            value={search}
            onChange={handleSearchChange}
            placeholder={LABELS.searchPlaceholder}
          />
        </Box>

        {/* Table */}
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: 3,
            boxShadow: '0px 1px 2px rgba(15, 23, 42, 0.06)',
            overflow: 'hidden',
            mx: 2,
          }}
        >
          <Table sx={{ minWidth: 900, borderCollapse: 'separate', borderSpacing: 0 }} size="small">
            <TableHead>
              <TableRow sx={TABLE_HEADER_ROW_SX}>
                {HEADER_CELLS.map((cell, index) => (
                  <TableCell
                    key={cell.key}
                    align={cell.align}
                    sx={{
                      ...(index === 0 && { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }),
                      ...(index === HEADER_CELLS.length - 1 && { borderTopRightRadius: 12, borderBottomRightRadius: 12 }),
                    }}
                  >
                    {cell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={HEADER_CELLS.length} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={HEADER_CELLS.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="error">
                      {LABELS.error}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={HEADER_CELLS.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {LABELS.noData}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((row) => {
                  const statusStyle = STATUS_STYLES[row.status];
                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        '& td': {
                          fontSize: 13,
                          color: '#111827',
                          borderBottomColor: '#F3F4F6',
                          py: 1.75,
                        },
                      }}
                    >
                      <TableCell>{row.historyDate}</TableCell>
                      <TableCell>{row.action}</TableCell>
                      <TableCell>{row.userName}</TableCell>
                      <TableCell align="right">{formatCurrency(row.totalAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.concessionAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.taxAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.netAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.paidAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.balanceAmount)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={statusStyle.label}
                          size="small"
                          variant="outlined"
                          sx={{
                            px: 1.5,
                            fontSize: 11,
                            fontWeight: 600,
                            borderRadius: 999,
                            borderColor: statusStyle.borderColor,
                            bgcolor: statusStyle.bgcolor,
                            color: statusStyle.color,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination */}
        <Box sx={{ mx: 2, mt: 2.5 }}>
          <InvoicePagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalEntries={totalCount}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={handlePageSizeChange}
          />
        </Box>
      </Box>
    </>
  );
};

export default InvoiceLogs;

